import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MapPin, Phone, Heart, MessageSquare, Users, CheckCircle2, BadgeCheck, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import api, { useTitle, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import ContactModal from "../components/ContactModal";

export default function ResourcePage() {
  const { slug, resourceId } = useParams();
  const [params] = useSearchParams();
  const date = params.get("date") || "";
  const [data, setData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactMode, setContactMode] = useState("contact");
  const [fav, setFav] = useState(false);
  const { user } = useAuth();
  useTitle(data?.resource?.name || "Kaynak");

  useEffect(() => {
    api.get(`/resources/${resourceId}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [resourceId]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-12"><div className="card h-96 animate-pulse" /></div>;
  const { resource, company, category, busy_dates } = data;
  const busyOnDate = date && busy_dates.includes(date);

  const track = (type) => api.post(`/companies/${company.id}/track`, { type }).catch(() => {});

  const toggleFav = async () => {
    if (!user) return toast.error("Favorilere eklemek için giriş yapın");
    const r = await api.post("/favorites/toggle", { resource_id: resource.id });
    setFav(r.data.favorited);
    toast.success(r.data.favorited ? "Favorilere eklendi" : "Favorilerden çıkarıldı");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="text-sm text-slate-500 mb-4">
        <Link to={`/firma/${company.slug}`} className="hover:text-[#D4AF37]">{company.name}</Link>
        <span className="mx-2">/</span>
        <span className="text-slate-300">{resource.name}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card overflow-hidden">
            <div className="h-64 sm:h-80 relative">
              <img src={resource.photos?.[0] || company.logo} alt={resource.name} className="w-full h-full object-cover" />
              {date && (
                <span className={`absolute top-4 right-4 ${busyOnDate ? "badge-red" : "badge-green"} backdrop-blur text-sm !px-4 !py-1.5`} data-testid="resource-date-status">
                  {busyOnDate ? "Bu tarihte DOLU" : "Bu tarihte MÜSAİT"}
                </span>
              )}
            </div>
            <div className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-extrabold text-white" data-testid="resource-name">{resource.name}</h1>
                  <div className="flex items-center gap-2 mt-1.5 text-sm text-slate-400">
                    <Link to={`/firma/${company.slug}`} className="flex items-center gap-1.5 hover:text-[#D4AF37]">
                      {company.name} {company.verified && <BadgeCheck size={14} className="text-[#D4AF37]" />}
                    </Link>
                    {category && <span className="badge-gold">{category.name}</span>}
                  </div>
                </div>
                {resource.max_capacity > 0 && (
                  <div className="badge-green !text-sm !px-4 !py-1.5"><Users size={14} /> {resource.min_capacity}-{resource.max_capacity} kişi</div>
                )}
              </div>
              {(resource.meal_yemekli || resource.meal_yemeksiz) && (
                <div className="flex gap-2 mt-3">
                  {resource.meal_yemekli && <span className="badge-gold" data-testid="meal-badge-yemekli">Yemekli hizmet</span>}
                  {resource.meal_yemeksiz && <span className="badge-gold" data-testid="meal-badge-yemeksiz">Yemeksiz hizmet</span>}
                </div>
              )}
              <p className="text-sm text-slate-300 leading-relaxed mt-4">{resource.description}</p>
              {resource.features?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {resource.features.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-slate-300">
                      <CheckCircle2 size={12} className="text-[#10B981]" /> {f}
                    </span>
                  ))}
                </div>
              )}
              {resource.video && (
                <div className="mt-5">
                  <h3 className="font-bold text-white mb-2">Tanıtım Videosu</h3>
                  <video data-testid="resource-video" src={resource.video} controls className="w-full max-h-80 rounded-xl border border-white/10" />
                </div>
              )}
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-4"><MapPin size={12} /> {resource.address || company.address}</div>
            </div>
          </div>

          <section>
            <h2 className="font-bold text-lg text-white mb-4">Müsaitlik Takvimi</h2>
            <AvailabilityCalendar busyDates={busy_dates} initialMonth={date ? date.slice(0, 7) : undefined} />
          </section>
        </div>

        <div className="space-y-4">
          <div className="card p-6 space-y-3 sticky top-24">
            <button data-testid="contact-request-btn" onClick={() => { setContactMode("contact"); setContactOpen(true); }} className="btn-gold w-full">
              <MessageSquare size={16} /> İletişime Geç
            </button>
            <button data-testid="reservation-request-btn" onClick={() => { setContactMode("reservation"); setContactOpen(true); }} className="btn-ghost w-full !border-[#D4AF37]/50 !text-[#D4AF37] hover:!bg-[#D4AF37]/10">
              <CalendarCheck size={16} /> Rezervasyon İsteği Gönder
            </button>
            {company.whatsapp && (
              <a data-testid="whatsapp-btn" href={`https://wa.me/${company.whatsapp}`} target="_blank" rel="noreferrer"
                onClick={() => track("whatsapp_click")}
                className="btn-ghost w-full !border-[#10B981]/40 !text-[#34D399]">WhatsApp'tan Yaz</a>
            )}
            {company.phone && (
              <a data-testid="phone-btn" href={`tel:${company.phone}`} onClick={() => track("phone_click")} className="btn-ghost w-full">
                <Phone size={16} /> Ara
              </a>
            )}
            <button data-testid="favorite-resource-btn" onClick={toggleFav}
              className={`btn-ghost w-full ${fav ? "!border-[#EF4444]/50 !text-red-400" : ""}`}>
              <Heart size={16} className={fav ? "fill-red-400" : ""} /> {fav ? "Favorilerde" : "Favorilere Ekle"}
            </button>
          </div>
        </div>
      </div>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} company={company} resource={resource} defaultDate={date} mode={contactMode} />
    </div>
  );
}
